"use client";

import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, DollarSign, Wrench, Paintbrush, Drill, ClipboardList, Shovel, CircleCheck, Zap, HardHat } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.15,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const expertiseAreas = [{
  id: "new-builds",
  title: "New Builds & Construction",
  description: "Comprehensive new build solutions for commercial and industrial sectors. We manage the entire construction lifecycle from initial site preparation to final handover with a focus on structural integrity.",
  pricing: "Full compliance with NCC and Australian Standards. We provide comprehensive project documentation, safety plans, and quality assurance certifications for all new builds.",
  prep: "• Commercial Developments\n• Industrial Warehousing\n• Structural Steel & Concrete\n• Architectural Construction\n• Turnkey Project Delivery",
  icon: HardHat,
  image: "https://bottomupprojects.com.au/wp-content/uploads/2026/05/BB-istockphoto-2225353379-612x612-1.jpg"
}, {
  id: "fitouts",
  title: "Commercial & Industrial Fit-outs",
  description: "Tailored fit-out solutions for operational excellence. We deliver complete environments for offices, warehouses, and retail hubs with precision joinery and integrated site safety installations.",
  pricing: "All projects adhere to strict NCC compliance and Australian Standards. We provide detailed risk assessments and safety management plans for every site.",
  prep: "• Office & Retail Fit-outs\n• Warehouse Solutions\n• Base Building Upgrades\n• Custom Joinery & Finishes\n• Site Safety Installations",
  icon: Wrench,
  image: "https://bottomupprojects.com.au/wp-content/uploads/2026/04/Commercial-Industrial-Fit-outs.jpg"
}, {
  id: "refurbishment",
  title: "Refurbishment & Interior Works",
  description: "for industrial, commercial and residential spaces, modernising spaces to improve functionality and efficiency. Our refurbishment works are delivered with precise planning to minimise operational downtime.",
  pricing: "Comprehensive structural assessments and performance upgrades. We focus on extending asset life through high-quality materials and expert workmanship.",
  prep: "• Functional Renovations\n• Efficiency Upgrades\n• Workspace Reconfigurations\n• Safety Compliance Audits\n• Material & Finish Selection",
  icon: Paintbrush,
  image: "https://bottomupprojects.com.au/wp-content/uploads/2026/04/Refurbishment-Interior-Works.jpg"
}, {
  id: "remedial",
  title: "Remedial, Restoration & Building Upgrades",
  description: "Specialised structural repairs and facility maintenance across commercial, industrial and residential properties. From façade restoration to waterproofing, we provide reliable solutions to protect and enhance your property assets.",
  pricing: "Proactive maintenance schedules and reactive repair services. Supported by advanced tracking systems for full accountability and compliance reporting.",
  prep: "• Structural & Concrete Repairs\n• Waterproofing Systems\n• Façade Restoration\n• Facility Maintenance Programmes\n• Asset Life Extension",
  icon: Drill,
  image: "https://bottomupprojects.com.au/wp-content/uploads/2026/04/Remedial-Maintenance-Services.jpg"
}, {
  id: "management",
  title: "Strategic Project Management",
  description: "End-to-end oversight ensuring quality, compliance, and risk mitigation. Our experienced team utilises industry-leading software for transparent reporting and stakeholder coordination.",
  pricing: "Structured defect management and quality assurance processes. We ensure high standards through meticulous inspections and real-time project tracking.",
  prep: "• End-to-End Project Delivery\n• Quality & Defect Management\n• Compliance & Risk Mitigation\n• Stakeholder Coordination\n• Detailed Progress Reporting",
  icon: ClipboardList,
  image: "https://bottomupprojects.com.au/wp-content/uploads/2026/04/Strategic-Project-Management.jpg"
}, {
  id: "civil-infrastructure",
  title: "Civil & Infrastructure",
  description: "Specialised civil engineering and infrastructure solutions for large-scale commercial and industrial developments. We manage complex site requirements with technical expertise and precision.",
  pricing: "Comprehensive engineering compliance and environmental management. We ensure all infrastructure works meet local council requirements and industrial load-bearing standards.",
  prep: "• Site Preparation & Grading\n• Drainage & Stormwater Systems\n• Pavement & Road Construction\n• Utility Trenching & Installation\n• Retaining Wall Systems",
  icon: HardHat,
  image: "https://bottomupprojects.com.au/wp-content/uploads/2026/04/Civil-Infrastructure.jpg"
}, {
  id: "demolition-earthworks",
  title: "DEMOLITION & EARTHWORKS",
  description: "Professional demolition and earthmoving services for site preparation and structural modifications, delivered with a focus on safety and efficiency.",
  pricing: "Strict adherence to safety protocols and environmental regulations. We provide comprehensive waste management and site clearance reports for every project.",
  prep: "• Site Clearance\n• Structural Demolition\n• Earthmoving & Excavation\n• Waste Management\n• Site Preparation",
  icon: Shovel,
  image: "https://bottomupprojects.com.au/wp-content/uploads/2026/04/DEMOLITION-EARTHWORKS.jpg"
}, {
  id: "defect-management",
  title: "Defect & Quality Control Management",
  description: "Comprehensive defect identification, rectification, and close-out services, including inspections, reporting, and coordination with stakeholders.",
  pricing: "Professional defect management ensuring high-quality outcomes and seamless project handovers through meticulous reporting and stakeholder alignment.",
  prep: "• Defect Identification\n• Rectification Services\n• Close-out Coordination\n• Detailed Inspections\n• Stakeholder Reporting",
  icon: CircleCheck,
  image: "https://bottomupprojects.com.au/wp-content/uploads/2026/04/DEFECT-MANAGEMENT.jpg"
}, {
  id: "facility-maintenance",
  title: "FACILITY SERVICES & MAINTENANCE",
  description: "Reliable maintenance solutions covering routine and complex works, supported by advanced systems for tracking, reporting, and compliance.",
  pricing: "Full-spectrum facility support designed to maintain asset value and ensure operational continuity through scheduled and reactive maintenance.",
  prep: "• Routine Maintenance\n• Complex Repair Works\n• Performance Tracking\n• Compliance Reporting\n• Asset Management Systems",
  icon: Zap,
  image: "https://bottomupprojects.com.au/wp-content/uploads/2026/04/facility_maintenance.webp"
}];
function ExpertiseRow({
  area,
  index
}: {
  area: typeof expertiseAreas[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const isEven = index % 2 === 0;
  const Icon = area.icon;
  return <div data-section-id="227297" data-index={index} ref={ref} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center py-16 lg:py-24 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      <div className={`flex flex-col items-start ${isEven ? "lg:order-1" : "lg:order-2"}`}>
        <div className="inline-flex items-center justify-center p-3 bg-primary/10 rounded-lg mb-6 text-primary">
          <Icon className="size-6" />
        </div>

        <h3 className="font-default text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground mb-6 leading-tight uppercase tracking-wider">
          {area.title}
        </h3>

        <p className="text-lg text-foreground/80 mb-8 leading-relaxed font-default">
          {area.description}
        </p>

        <Sheet>
          <SheetTrigger asChild>
            <Button size="lg" className="rounded-lg shadow-md hover:shadow-lg transition-all group bg-primary text-primary-foreground">
              More Info
              <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </SheetTrigger>

          <SheetContent side="right" className="sm:max-w-md w-full overflow-y-auto border-l border-border bg-background shadow-2xl">
            <SheetHeader className="mb-8 text-left">
              <div className="inline-flex items-center justify-center p-3 bg-primary/10 rounded-lg mb-4 w-fit text-primary">
                <Icon className="size-6" />
              </div>

              <SheetTitle className="font-default text-2xl md:text-3xl text-foreground font-extrabold uppercase tracking-tight">
                {area.title}
              </SheetTitle>

              <SheetDescription className="text-base mt-2 font-mono text-muted-foreground">
                Technical specifications and project delivery frameworks.
              </SheetDescription>
            </SheetHeader>

            <div className="space-y-8">
              <div className="bg-muted/50 p-6 rounded-lg border border-border">
                <h4 className="font-default text-sm font-bold flex items-center gap-2 mb-3 text-foreground uppercase tracking-widest">
                  <DollarSign className="size-5 text-primary" /> Compliance & Quality Assurance
                </h4>

                <p className="text-foreground/80 leading-relaxed font-default text-sm">
                  {area.pricing}
                </p>

                <p className="text-xs text-muted-foreground mt-4 italic font-mono">
                  *All works are fully insured and delivered by licensed Australian trade professionals.
                </p>
              </div>

              <div className="bg-primary/5 p-6 rounded-lg border border-primary/20">
                <h4 className="font-default text-sm font-bold flex items-center gap-2 mb-3 text-foreground uppercase tracking-widest">
                  <Zap className="size-5 text-primary" /> Technical Specifications
                </h4>

                <div className="text-foreground/80 leading-relaxed font-mono text-sm whitespace-pre-line">
                  {area.prep}
                </div>
              </div>

              <div className="pt-6 border-t border-border/50">
                <div className="space-y-4"></div>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>

      <div className={`relative aspect-video lg:aspect-square overflow-hidden rounded-lg shadow-md ${isEven ? "lg:order-2" : "lg:order-1"}`}>
        <img src={area.image} alt={area.title} className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" loading="lazy" decoding="async" />
        <div className="absolute inset-0 bg-primary/5 pointer-events-none" />
      </div>
    </div>;
}
export default function OurClinicalExpertise() {
  return <section id="our-specialised-solutions" className="bg-background text-foreground relative overflow-hidden py-24 sm:py-32">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] opacity-10 pointer-events-none bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary via-background to-background -z-10" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="max-w-3xl mx-auto text-center mb-16 lg:mb-24">
          <div className="inline-block px-3 py-1 bg-primary/10 text-primary font-mono text-xs font-bold tracking-widest uppercase mb-4 rounded-lg">
            Core Services
          </div>

          <h2 className="font-default text-4xl md:text-5xl lg:text-7xl font-extrabold text-foreground tracking-tighter mb-6 uppercase">
            Our Specialised Solutions
          </h2>

          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed font-default max-w-2xl mx-auto">
            Bottom Up Projects delivers high-quality construction, fit-out, and remedial solutions across commercial and industrial sectors with a focus on compliance and reliability.
          </p>
        </div>

        <div className="flex flex-col divide-y divide-border/20">
          {expertiseAreas.map((area, index) => <ExpertiseRow key={area.id} area={area} index={index} />)}
        </div>
      </div>
    </section>;
}